/* eslint-disable react/button-has-type */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ResultBox from './ResultBox';
import ModifierSelector from './ModifierSelector';
import './Maps.css';
import tradeData from '../../data/map-trade.json';
import generateMapTradeUrl from '../../utils/generateMapTradeUrl';

const Maps = () => {
	const [wantedMods, setWantedMods] = useState([]);
	const [unwantedMods, setUnwantedMods] = useState([]);
	const [allModifiers, setAllModifiers] = useState([]);
	const [isT17Selected, setIsT17Selected] = useState(false);
	const [regex, setRegex] = useState('');
	const [error, setError] = useState('');
	const [warning, setWarning] = useState('');
	const [itemQuantity, setItemQuantity] = useState('');
	const [packSize, setPackSize] = useState('');
	const [itemRarity, setItemRarity] = useState('');
	const [mapDrop, setMapDrop] = useState('');
	const [scarab, setScarab] = useState('');
	const [currency, setCurrency] = useState('');
	const [divination, setDivination] = useState('');
	const [corrupted, setCorrupted] = useState(false); // false: 不篩選, true: 已汙染
	const [rarity, setRarity] = useState([]); // ['normal', 'magic', 'rare']
	const [allGoodMods, setAllGoodMods] = useState(false);
	const generateRequestId = useRef(0);
	const [tradeLeague, setTradeLeague] = useState(() => {
		try {
			const saved = localStorage.getItem('map-trade-league');

			if (saved) return tradeData.leagues.some(league => league.id === saved) ? saved : '';
		} catch (_) { /* 儲存不可用時仍可開啟交易搜尋。 */ }

		return tradeData.leagues[0]?.id || '';
	});
	let tradeUrl = '';
	let tradeError = '';

	try {
		tradeUrl = generateMapTradeUrl({
			wantedMods, unwantedMods, allGoodMods, itemQuantity, packSize, itemRarity,
			mapDrop, scarab, currency, divination, corrupted, rarity,
		}, tradeLeague);
	} catch (err) {
		tradeError = err.message;
	}

	const changeTradeLeague = (event) => {
		const league = event.target.value;

		setTradeLeague(league);

		try { localStorage.setItem('map-trade-league', league); } catch (_) { /* 不影響當次搜尋。 */ }
	};

	const getApiUrl = useCallback((endpoint) => {
		const baseUrl = process.env.REACT_APP_API_URL || '';

		console.log('原始 API URL 基礎路徑:', baseUrl);

		// 如果 baseUrl 為空，使用相對路徑（適用於 Vercel 生產環境）
		if (!baseUrl) {
			return `/api/v1/${endpoint}`;
		}

		// 檢查 baseUrl 是否已經包含 /api/v1
		if (baseUrl.includes('/api/v1')) {
			// 如果已經包含，則直接添加端點
			return `${baseUrl}/${endpoint}`;
		}

		// 如果不包含，則添加 /api/v1 和端點
		return `${baseUrl}/api/v1/${endpoint}`;
	}, []);

	const fetchModifiers = useCallback(async () => {
		try {
			const apiUrl = getApiUrl('maps?type=1');

			console.log('獲取地圖詞綴的 API URL:', apiUrl);

			const response = await fetch(apiUrl);

			if (!response.ok) {
				throw new Error(`網絡響應錯誤: ${response.status} ${response.statusText}`);
			}

			const data = await response.json();

			return Object.keys(data).map(mod => ({ mod, isT17: false })) || [];
		} catch (error) {
			console.error('無法獲取地圖詞綴:', error);
			setError('無法載入詞綴數據，請檢查網路連接或重新整理頁面');
			return [];
		}
	}, [getApiUrl]);

	const fetchT17Modifiers = useCallback(async () => {
		try {
			const apiUrl = getApiUrl('maps/t17?type=2');

			console.log('獲取 T16.5/夢魘地圖詞綴的 API URL:', apiUrl);

			const response = await fetch(apiUrl);

			if (!response.ok) {
				throw new Error(`網絡響應錯誤: ${response.status} ${response.statusText}`);
			}

			const data = await response.json();

			return Object.keys(data).map(mod => ({ mod, isT17: true })) || [];
		} catch (error) {
			console.error('Failed to fetch T17 modifiers:', error);
			setError('無法載入 T16.5/夢魘詞綴數據，請檢查網路連接或重新整理頁面');
			return [];
		}
	}, [getApiUrl]);

	useEffect(() => {
		const getInitialModifiers = async () => {
			const mods = await fetchModifiers();

			setAllModifiers(mods);
		};

		getInitialModifiers();
	}, [fetchModifiers]);

	const handleT17CheckboxChange = async checked => {
		setIsT17Selected(checked);

		if (checked) {
			const t17Mods = await fetchT17Modifiers();

			setAllModifiers(prevMods => {
				const combinedMods = [...t17Mods, ...prevMods];

				return combinedMods.filter((mod, index, self) =>
					index === self.findIndex(t => t.mod === mod.mod),
				);
			});
		} else {
			const regularMods = await fetchModifiers();

			setAllModifiers(regularMods);
		}
	};

	const regenerate = useCallback(async (overrides = {}) => {
		const requestId = ++generateRequestId.current;
		const values = {
			wantedMods,
			unwantedMods,
			itemQuantity,
			packSize,
			itemRarity,
			mapDrop,
			scarab,
			currency,
			divination,
			corrupted,
			rarity,
			allGoodMods,
			...overrides,
		};

		try {
			const apiUrl = getApiUrl('maps/generateMapRegex');
			const response = await fetch(apiUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					...values,
					itemQuantity: values.itemQuantity ? parseInt(values.itemQuantity, 10) : undefined,
					packSize: values.packSize ? parseInt(values.packSize, 10) : undefined,
					itemRarity: values.itemRarity ? parseInt(values.itemRarity, 10) : undefined,
					mapDrop: values.mapDrop ? parseInt(values.mapDrop, 10) : undefined,
					scarab: values.scarab ? parseInt(values.scarab, 10) : undefined,
					currency: values.currency ? parseInt(values.currency, 10) : undefined,
					divination: values.divination ? parseInt(values.divination, 10) : undefined,
				}),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();
			const newRegex = data.regex;

			if (requestId !== generateRequestId.current) return;

			setRegex(newRegex);
			setError('');
		} catch (error) {
			if (requestId !== generateRequestId.current) return;

			console.error('生成正則表達式時出錯：', error);
			setError('生成正則表達式時發生錯誤');
		}
	}, [wantedMods, unwantedMods, itemQuantity, packSize, itemRarity, mapDrop, scarab, currency, divination, corrupted, rarity, allGoodMods, getApiUrl]);

	const handleReset = () => {
		generateRequestId.current += 1;
		setWantedMods([]);
		setUnwantedMods([]);
		setRegex('');
		setError('');
		setWarning('');
		setItemQuantity('');
		setPackSize('');
		setItemRarity('');
		setMapDrop('');
		setScarab('');
		setCurrency('');
		setDivination('');
		setCorrupted(false);
		setRarity([]);
		setIsT17Selected(false);
		fetchModifiers().then(mods => setAllModifiers(mods));
	};

	const handleClearItemQuantity = React.useCallback(() => {
		setItemQuantity('');
		regenerate({ itemQuantity: '' });
	}, [regenerate]);

	const handleClearPackSize = React.useCallback(() => {
		setPackSize('');
		regenerate({ packSize: '' });
	}, [regenerate]);

	const handleClearItemRarity = React.useCallback(() => {
		setItemRarity('');
		regenerate({ itemRarity: '' });
	}, [regenerate]);

	const handleClearMapDrop = React.useCallback(() => {
		setMapDrop('');
		regenerate({ mapDrop: '' });
	}, [regenerate]);

	const handleClearScarab = React.useCallback(() => {
		setScarab('');
		regenerate({ scarab: '' });
	}, [regenerate]);

	const handleClearCurrency = React.useCallback(() => {
		setCurrency('');
		regenerate({ currency: '' });
	}, [regenerate]);

	const handleClearDivination = React.useCallback(() => {
		setDivination('');
		regenerate({ divination: '' });
	}, [regenerate]);

	return (
		<div className="maps-container">
			<div>
				<ResultBox
					result={regex}
					warning={warning}
					error={error}
					reset={handleReset}
					maxLength={250}
				/>
				<div className="map-trade-controls">
					<label htmlFor="map-trade-league">台服聯盟</label>
					<select id="map-trade-league" value={tradeLeague} onChange={changeTradeLeague}>
						<option value="" disabled>請重新選擇聯盟</option>
						{tradeData.leagues.map(league => <option key={league.id} value={league.id}>{league.text}</option>)}
					</select>
					{tradeUrl
						? <a className="map-trade-link" href={tradeUrl} target="_blank" rel="noopener noreferrer">交易市集 ↗</a>
						: <button className="map-trade-link" disabled aria-describedby="map-trade-error">交易市集 ↗</button>}
					<span className="map-trade-note">即刻購買 · T16 以上</span>
				</div>
				{tradeError && <p id="map-trade-error" className="map-trade-error" role="status">{tradeError}。遊戲內搜尋不受影響。</p>}
				<div className="quantity-inputs">
					<div className="input-row">
					<div className="item-quantity-input">
						<label htmlFor="itemQuantity">物品數量至少為：</label>
						<div className="input-with-clear">
							<input
								type="number"
								id="itemQuantity"
								value={itemQuantity}
								min="0"
								onChange={e => {
									setItemQuantity(e.target.value);
									regenerate({ itemQuantity: e.target.value });
								}}
							/>
							{itemQuantity && (
								<button className="clear-button" onClick={handleClearItemQuantity}>
									x
								</button>
							)}
						</div>
					</div>
					<div className="pack-size-input">
						<label htmlFor="packSize">怪群大小至少為：</label>
						<div className="input-with-clear">
							<input
								type="number"
								id="packSize"
								value={packSize}
								min="0"
								onChange={e => {
									setPackSize(e.target.value);
									regenerate({ packSize: e.target.value });
								}}
							/>
							{packSize && (
								<button className="clear-button" onClick={handleClearPackSize}>
									x
								</button>
							)}
						</div>
					</div>
						<div className="item-rarity-input">
							<label htmlFor="itemRarity">物品稀有度至少為：</label>
							<div className="input-with-clear">
								<input
									type="number"
									id="itemRarity"
									value={itemRarity}
									min="0"
									onChange={e => {
										setItemRarity(e.target.value);
										regenerate({ itemRarity: e.target.value });
									}}
								/>
								{itemRarity && (
									<button className="clear-button" onClick={handleClearItemRarity}>
										x
									</button>
								)}
							</div>
						</div>
					</div>
					<div className="input-row">
						<div className="map-drop-input">
							<label htmlFor="mapDrop">更多地圖至少為：</label>
							<div className="input-with-clear">
								<input
									type="number"
									id="mapDrop"
									value={mapDrop}
									min="0"
									onChange={e => {
										setMapDrop(e.target.value);
										regenerate({ mapDrop: e.target.value });
									}}
								/>
								{mapDrop && (
									<button className="clear-button" onClick={handleClearMapDrop}>
										x
									</button>
								)}
							</div>
						</div>
						<div className="scarab-input">
							<label htmlFor="scarab">更多聖甲蟲至少為：</label>
							<div className="input-with-clear">
								<input
									type="number"
									id="scarab"
									value={scarab}
									min="0"
									onChange={e => {
										setScarab(e.target.value);
										regenerate({ scarab: e.target.value });
									}}
								/>
								{scarab && (
									<button className="clear-button" onClick={handleClearScarab}>
										x
									</button>
								)}
							</div>
						</div>
						<div className="currency-input">
							<label htmlFor="currency">更多通貨至少為：</label>
							<div className="input-with-clear">
								<input
									type="number"
									id="currency"
									value={currency}
									min="0"
									onChange={e => {
										setCurrency(e.target.value);
										regenerate({ currency: e.target.value });
									}}
								/>
								{currency && (
									<button className="clear-button" onClick={handleClearCurrency}>
										x
									</button>
								)}
							</div>
						</div>
					</div>
					<div className="input-row">
						<div className="divination-input">
							<label htmlFor="divination">更多命運卡至少為：</label>
							<div className="input-with-clear">
						<input
									type="number"
									id="divination"
									value={divination}
									min="0"
									onChange={e => {
										setDivination(e.target.value);
										regenerate({ divination: e.target.value });
									}}
								/>
								{divination && (
									<button className="clear-button" onClick={handleClearDivination}>
										x
									</button>
								)}
							</div>
						</div>
						<div className="corrupted-input">
							<button
								onClick={() => {
									const newCorrupted = !corrupted;

									setCorrupted(newCorrupted);
									regenerate({ corrupted: newCorrupted });
								}}
								className={corrupted ? 'active' : ''}
							>
								已汙染
							</button>
						</div>
						<div className="rarity-input">
							<div className="rarity-buttons">
								<button
									onClick={() => {
										const newRarity = rarity.includes('normal')
											? rarity.filter(r => r !== 'normal')
											: [...rarity, 'normal'];

										setRarity(newRarity);
										regenerate({ rarity: newRarity });
									}}
									className={`rarity-normal ${rarity.includes('normal') ? 'active' : ''}`}
								>
									普通
								</button>
								<button
									onClick={() => {
										const newRarity = rarity.includes('magic')
											? rarity.filter(r => r !== 'magic')
											: [...rarity, 'magic'];

										setRarity(newRarity);
										regenerate({ rarity: newRarity });
									}}
									className={`rarity-magic ${rarity.includes('magic') ? 'active' : ''}`}
								>
									魔法
								</button>
								<button
									onClick={() => {
										const newRarity = rarity.includes('rare')
											? rarity.filter(r => r !== 'rare')
											: [...rarity, 'rare'];

										setRarity(newRarity);
										regenerate({ rarity: newRarity });
									}}
									className={`rarity-rare ${rarity.includes('rare') ? 'active' : ''}`}
								>
									稀有
								</button>
							</div>
						</div>
					</div>
					<div className="options-row">
						<button
							onClick={() => handleT17CheckboxChange(!isT17Selected)}
							className={`t17-button ${isT17Selected ? 'active' : ''}`}
						>
							t16.5/夢魘詞綴
						</button>
					<div className="all-good-mods-toggle">
                        <button
                            onClick={() => {
								setAllGoodMods(false);
								regenerate({ allGoodMods: false });
							}}
                            className={!allGoodMods ? 'active' : ''}
                        >
							任一詞即可
                        </button>
                        <button
                            onClick={() => {
								setAllGoodMods(true);
								regenerate({ allGoodMods: true });
							}}
                            className={allGoodMods ? 'active' : ''}
                        >
							詞全對才亮
                        </button>
						</div>
                    </div>
				</div>
				<div className="modifiers-container">
					<ModifierSelector
						title="不要的詞...(不會高亮！)"
						modifiers={allModifiers}
						selectedMods={unwantedMods}
						onSelectMod={mod => {
							const newUnwantedMods = [...unwantedMods, mod];

							setUnwantedMods(newUnwantedMods);
							regenerate({ unwantedMods: newUnwantedMods });
						}}
						onDeselectMod={mod => {
							const newUnwantedMods = unwantedMods.filter(m => m.mod !== mod.mod);

							setUnwantedMods(newUnwantedMods);
							regenerate({ unwantedMods: newUnwantedMods });
						}}
					/>
					<ModifierSelector
						title="需要的詞"
						modifiers={allModifiers}
						selectedMods={wantedMods}
						onSelectMod={mod => {
							const newWantedMods = [...wantedMods, mod];

							setWantedMods(newWantedMods);
							regenerate({ wantedMods: newWantedMods });
						}}
						onDeselectMod={mod => {
							const newWantedMods = wantedMods.filter(m => m.mod !== mod.mod);

							setWantedMods(newWantedMods);
							regenerate({ wantedMods: newWantedMods });
						}}
					/>

				</div>
			</div>
		</div>

	);
};

export default Maps;
